import { QueryClient, dehydrate } from "@tanstack/react-query";
import { NextPageContext } from "next";
import { Errors } from "../types/api";
import { UNAVAILABLE_REASON, UNAVAILABLE_RESOURCE_TYPE } from "../types/unavailable_resource_type";
import { logAxiosError, logError } from "../logging/logSentryError";
import { isAxiosError, toError } from "./toError";
import { APPLICATION_ROUTES } from "../shared-logic/routes";
import { isLoginPath } from "./session";

/**
 * Sets the status on the response and builds the props that render the unavailable page in place,
 * so a resource that cannot be shown never redirects and never answers 200.
 * @param ctx The page context, its response carries the status code
 * @param queryClient The query client dehydrated into the props, null on routes that fetch nothing
 * @param resourceType The resource that was asked for, selects the body copy
 * @param reason Why the resource is unavailable, selects the body
 * @param statusCode The status to send, 404 unless the resource is only gated by a link or a password
 * @returns The server side props rendering the unavailable page
 */
export function unavailableResponse(
    ctx: NextPageContext,
    queryClient: QueryClient | null,
    resourceType: UNAVAILABLE_RESOURCE_TYPE,
    reason: UNAVAILABLE_REASON,
    statusCode: number = Errors.NotFound
) {
    const { props } = statusResponse(ctx, statusCode);

    return {
        props: {
            ...props,
            ...(queryClient ? { dehydratedState: dehydrate(queryClient) } : {}),
            unavailableResourceType: resourceType,
            unavailableReason: reason
        }
    };
}

/**
 * Sets a bare status on the response, for pages with no resource specific body to show.
 * A 404 renders the generic not found page, a 500 the generic error page.
 * @param ctx The page context, its response carries the status code
 * @param statusCode The status to send
 * @returns The server side props rendering the matching generic page
 */
export function statusResponse(ctx: NextPageContext, statusCode: number) {
    if (ctx.res) {
        ctx.res.statusCode = statusCode;
    }

    return { props: { statusCode } };
}

/**
 * Sends the user to login and back to the requested page afterwards.
 * Never redirects from login itself, that would loop.
 * @param ctx The server side props context, its resolved url is the page to return to
 * @returns The server side props redirect to login, null when already on a login page
 */
function loginRedirect(ctx: NextPageContext) {
    const returnTo = "resolvedUrl" in ctx && typeof ctx.resolvedUrl === "string" ? ctx.resolvedUrl : "";
    const [pathname] = returnTo.split("?");

    if (isLoginPath(pathname)) return null;

    const query = returnTo ? `?redirect=${encodeURIComponent(returnTo)}` : "";

    return { redirect: { destination: `${APPLICATION_ROUTES.LOGIN}${query}`, permanent: false } };
}

/**
 * Answers a failure thrown while building a public page. An unauthorized from the API sends the user to login.
 * A not found or access denied is a genuine 404, anything else is a fault on our side and must stay a 500
 * so the url is not dropped from the index.
 * @param ctx The page context, its response carries the status code
 * @param queryClient The query client dehydrated into the props, null on routes that fetch nothing
 * @param resourceType The resource that was asked for, selects the body copy on the 404 branch
 * @param error The caught value
 * @returns The server side props redirecting to login, or rendering the unavailable page or the error page
 */
export async function serverSideErrorResponse(
    ctx: NextPageContext,
    queryClient: QueryClient | null,
    resourceType: UNAVAILABLE_RESOURCE_TYPE,
    error: unknown
) {
    const apiStatus = isAxiosError(error) ? error.response?.status : undefined;

    const redirect = apiStatus === Errors.Unauthorized ? loginRedirect(ctx) : null;
    if (redirect) return redirect;

    if (apiStatus === Errors.NotFound || apiStatus === Errors.AccessDenied) {
        return unavailableResponse(ctx, queryClient, resourceType, UNAVAILABLE_REASON.NOT_FOUND);
    }

    if (isAxiosError(error)) {
        await logAxiosError(error);
    } else {
        await logError(toError(error));
    }

    return statusResponse(ctx, Errors.InternalServerError);
}
